import React, { useState } from 'react'; 
import { useTranslation } from "react-i18next";
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "./ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navItems = [
    { key: "home", path: "/" },
    { key: "about", path: "/about" },
    { key: "register", path: "/register" },
    { key: "contact", path: "/contact" }
  ];

  const isActive = (path) => location.pathname === path;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-6">
            <img 
              src="logo2.png" 
              alt="Steppe Rise" 
              className="h-11 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className={`font-medium transition-colors hover:text-yellow-600 ${
                  isActive(item.path)
                    ? 'text-yellow-600 border-b-2 border-yellow-600 pb-1'
                    : 'text-gray-800'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {/* Language Switcher Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-2 px-3 py-2 border rounded-full hover:bg-gray-100 transition">
                  <img 
                    src={`/flags/${i18n.language}.png`} 
                    alt={i18n.language} 
                    className="w-5 h-5 rounded-full"
                  />
                  <span className="uppercase font-medium">{i18n.language}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-40">
                <DropdownMenuItem onClick={() => changeLanguage("en")}>
                  <img src="/flags/en.png" alt="EN" className="w-5 h-5 mr-2 rounded-full" />
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage("ru")}>
                  <img src="/flags/ru.png" alt="RU" className="w-5 h-5 mr-2 rounded-full" />
                  Русский
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage("kz")}>
                  <img src="/flags/kz.png" alt="KZ" className="w-5 h-5 mr-2 rounded-full" />
                  Қазақша
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button */}
            <Button 
              asChild
              className="bg-black hover:bg-gray-800 text-yellow-400 font-semibold px-6 py-2 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg border border-yellow-400"
            >
              <Link to="/register">{t("nav.applyNow")}</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-4 border-t border-gray-200 bg-white/95 backdrop-blur-lg">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className={`block font-medium py-2 transition-colors hover:text-yellow-600 ${
                  isActive(item.path) ? 'text-yellow-600' : 'text-gray-800'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}

            {/* Mobile Language Switcher */}
            <div className="flex justify-center space-x-4 mt-4">
              <button onClick={() => changeLanguage("en")} className="flex items-center px-3 py-2 border rounded-full hover:bg-gray-100">
                <img src="/flags/en.png" alt="EN" className="w-5 h-5 mr-2 rounded-full" />
                EN
              </button>
              <button onClick={() => changeLanguage("ru")} className="flex items-center px-3 py-2 border rounded-full hover:bg-gray-100">
                <img src="/flags/ru.png" alt="RU" className="w-5 h-5 mr-2 rounded-full" />
                RU
              </button>
              <button onClick={() => changeLanguage("kz")} className="flex items-center px-3 py-2 border rounded-full hover:bg-gray-100">
                <img src="/flags/kz.png" alt="KZ" className="w-5 h-5 mr-2 rounded-full" />
                KZ
              </button>
            </div>

            <Button 
              asChild
              className="w-full bg-black hover:bg-gray-800 text-yellow-400 font-semibold mt-4 border border-yellow-400"
            >
              <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                {t("nav.applyNow")}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
